import styled from '@emotion/styled';
import { AppUI, ExerciseUI, ForumUI } from '@seedlang/state';
import { isPresent, isBlank } from '@seedlang/utils';
import { noop } from 'lodash';
import CommentIndex from 'components/comment/comment_index';
import ExerciseContent from 'components/exercise/exercise_content';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import ExerciseNavigation from 'components/worksheet/exercise_navigation';
import NoAccessButton from 'components/worksheet/no_access_button';
import { computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import Link from 'components/link';
import withRouteParams from 'utils/with_route_params';
import { getPodcastAccessSubText } from 'utils/podcast_access_disclaimer';

const Wrapper = styled.div`
  .fa-angle-left {
    font-weight: bold;
    margin-right: 5px;
    font-size: 20px;
    line-height: 25px;
  }
`;

const Content = styled.div`
  background: white;
  padding: 20px;
  border-radius: ${props => props.borderRadius}; 
`;

const Exercise = styled.div`

`;

const AdminLink = styled.div`
  margin-top: 5px;
  font-size: 11px;
  text-decoration: underline;
  text-align: center;
  padding: 5px;
`;

const InnerWrapper = styled.div`
  display: grid;
  grid-template-columns: 18% 80%;
  gap: 20px;
  @media (max-width: 899px) {
    display: flex;
    flex-direction: column;
  }
  height: ${props => props.hasAccess ? null : '250px'};
  mask-image: ${props => props.hasAccess ? null : 'linear-gradient(to bottom,black,70%,transparent)'};
  pointer-events: ${props => props.hasAccess ? 'auto' : 'none'};
  user-select: ${props => props.hasAccess ? 'auto' : 'none'};
  overflow: ${props => props.hasAccess ? 'visible' : 'hidden'};
`;

@observer
class ExerciseShow extends React.Component {
  @observable showUserSettings;
  @observable comment = '';
  @observable showRatingComment = false;
  @observable ratingCommentSubmitted = false;
  @observable showSpinner = true;

  componentDidMount() {
    this.initializeExercise();
    if (!AppUI.membershipTypeStore.hasIndexData) {
      AppUI.membershipTypeStore.getIndex();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.params.exerciseId !== this.props.params.exerciseId || this.exerciseParamResolvedAfterWorksheetLoad) {
      this.initializeExercise();
    }
  }

  @computed get routeExercise() {
    return ExerciseUI.findWorksheetExerciseByParam(this.props.params.exerciseId);
  }

  @computed get resolvedExerciseId() {
    return this.routeExercise?.id || this.props.params.exerciseId;
  }

  @computed get exerciseParamResolvedAfterWorksheetLoad() {
    return this.props.params.worksheetId && ExerciseUI.hasWorksheet && ExerciseUI.exerciseId !== this.resolvedExerciseId;
  }

  @computed get routeExerciseIsMissing() {
    return this.props.params.worksheetId && ExerciseUI.hasWorksheet && isBlank(this.routeExercise) && isPresent(ExerciseUI.worksheet?.exercises?.[0]);
  }

  @computed get podcastAccessSubText() {
    return getPodcastAccessSubText(ExerciseUI.worksheet, AppUI.membershipTypeStore.indexData);
  }

  routeToFirstExercise = () => {
    const routeName = `${ExerciseUI.worksheetNamespace}.exercises.show`;
    const params = {
      worksheetId: this.props.params.worksheetId,
      exerciseId: ExerciseUI.routeParamForExercise(ExerciseUI.worksheet.exercises[0], routeName),
    };

    AppUI.routeStore.router.replace({
      name: routeName,
      params: params,
    });
    AppUI.routeStore.setRouteName(routeName);
    AppUI.routeStore.params = params;
  }

  initializeExercise = () => {
    if (this.props.params.worksheetId && !ExerciseUI.hasWorksheet) {
      this.showSpinner = true;
      return;
    }
    if (this.routeExerciseIsMissing) {
      this.routeToFirstExercise();
      return;
    }
    ExerciseUI.set('exerciseId', this.resolvedExerciseId);
    const worksheetExercise = this.routeExercise;
    const alreadyEnriched = worksheetExercise?.enriched;
    ExerciseUI.enrichExercise(this.resolvedExerciseId, this.afterEnrichExercise);
    this.showRatingComment = false;
    this.ratingCommentSubmitted = false;
    this.comment = '';
    if (alreadyEnriched) {
      this.afterEnrichExercise();
    }
  }

  afterEnrichExercise = () => {
    AppUI.scrollToPageTop('auto');
    this.showSpinner = false;
    if (isBlank(ExerciseUI.worksheetUserExerciseStore.indexData) || !ExerciseUI.worksheetUserExerciseStore.indexData?.find(item => item.exerciseId === this.resolvedExerciseId)) {
      ExerciseUI.worksheetUserExerciseStore.getIndex({ids: {worksheetId: ExerciseUI.worksheet?.id || this.props.params.worksheetId}});
    }
  }

  componentWillUnmount() {
    ExerciseUI.set('exerciseId', null);
  }

  topIsInView = (el) => {
    const rect = el.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.top <= (window.innerHeight || document.documentElement.clientHeight)
    );
  }

  setComment = (comment) => {
    this.comment = comment;
  }

  afterSubmitComment = () => {
    this.commentTimestamp = +new Date();
  }

  goBackToTop = () => {
    AppUI.scrollToPageTop();
  }

  onSubmitRating = (rating) => {
    this.showRatingComment = true;
    ExerciseUI.onClickRating(rating);
  }

  onSubmitRatingComment = () => {
    ExerciseUI.onSubmitRatingComment(this.comment);
    this.ratingCommentSubmitted = true;
    this.comment = '';
  }

  @computed get contentBorderRadius() {
    if (ExerciseUI.layout.isMobile) {
      return '0';
    }
    if (ExerciseUI.exercise?.forumThread && ExerciseUI.site.showWorksheetComments) {
      if (this.props.tabCount <= 1) {
        return '10px 10px 0 0';
      }
      return '0px 10px 0 0';
    } if (this.props.tabCount <= 1) {
      return '10px';
    }
    return '0 10px 10px 10px';
  }

  @computed get commentsBorderRadius() {
    if (ExerciseUI.layout.isMobile) {
      return '0';
    }
    return '0 0 10px 10px';
  }

  goToNextExercise = () => {
    ExerciseUI.goToNextExercise(() => AppUI.scrollToPageTop('auto'), ExerciseUI.hasWorksheet ? `${ExerciseUI.worksheetNamespace}.exercises.show` : 'exercises.show');
  }

  toggleEnglishPrompts = () => {
    ExerciseUI.authUserStore.update({data: { hideEnglishPrompts: !ExerciseUI.user.hideEnglishPrompts }}, noop);
    ExerciseUI.user.set('hideEnglishPrompts', !ExerciseUI.user.hideEnglishPrompts);
  }

  render() {
    return (
      <Wrapper>
        <Content
          borderRadius={this.contentBorderRadius}
        >
          <InnerWrapper
            hasAccess={ExerciseUI.worksheet?.accessStatus?.hasExerciseAccess}
          >
            <ExerciseNavigation
              padding='0'
              margin='0 0 20px 0'
              exercises={ExerciseUI.hasWorksheet && ExerciseUI.worksheetStore.showData.exercises.filter(item => item.exerciseType.slug !== null)}
              currentExerciseId={ExerciseUI.exercise?.id}
              worksheetId={this.props.params.worksheetId}
              useWorksheetRoute={ExerciseUI.hasWorksheet}
              afterRouteToExercise={this.scrollToTop}
              hasAccess={ExerciseUI.worksheet?.accessStatus?.hasExerciseAccess}
            />
          {
            ExerciseUI.hasExercise &&
              <Exercise className='exercise-wrapper'>
                <ExerciseContent
                  toggleEnglishPrompts={this.toggleEnglishPrompts}
                  goToNextExercise={this.goToNextExercise}
                  showRatingComment={this.showRatingComment}
                  goBackToTop={this.goBackToTop}
                  onSubmitRating={this.onSubmitRating}
                  onSubmitRatingComment={this.onSubmitRatingComment}
                  setComment={this.setComment}
                  comment={this.comment}
                  ratingCommentSubmitted={this.ratingCommentSubmitted}
                />
              </Exercise>
          }
          </InnerWrapper>
          {
            ExerciseUI.hasWorksheet && !ExerciseUI.worksheet?.accessStatus?.hasExerciseAccess &&
              <NoAccessButton
                reason={ExerciseUI.worksheet?.accessStatus?.reason}
                subText={this.podcastAccessSubText}
              />
          }
        </Content>
        {
          ExerciseUI.exercise?.forumThread && ExerciseUI.site.showWorksheetComments &&
            <CommentIndex
              hideUserProfile
              showCommentCreate={ExerciseUI.worksheet?.accessStatus?.hasExerciseAccess}
              commentTimestamp={this.commentTimestamp}
              groupId={ExerciseUI.exercise.worksheet.groupId}
              inModal={false}
              post={ExerciseUI.exercise.forumThread}
              showCommentCount
              user={ForumUI.user}
              commentable={ExerciseUI.exercise.forumThread}
              commentableType='ForumThread'
              forumThreadId={ExerciseUI.exercise.forumThread?.id}
              margin='0'
              languageId={ExerciseUI.site.languageId}
              borderRadius={this.commentsBorderRadius}
              padding='40px 20px 20px 20px'
              commentPadding='20px'
              placeholder='Leave a comment about the exercise'
              indentation={ExerciseUI.layout.isMobile ? 20 : 90}
            />
        }
        {
          ExerciseUI.user.anyAdmin && ExerciseUI.exercise && ExerciseUI.exercise.worksheet && ExerciseUI.exercise.worksheet.groupId &&
            <AdminLink>
              <Link
                to={{name: 'creator.exercises.edit', params: {groupId: ExerciseUI.exercise.worksheet.groupId, worksheetId: ExerciseUI.exercise.worksheet.id, exerciseId: ExerciseUI.exercise.id}}}
              >
                Admin Link
              </Link>
            </AdminLink>
        }
      </Wrapper>
    );
  }
}

export default withRouteParams(ErrorBoundary(ExerciseShow));
